/* eslint-disable max-lines-per-function */
import { RenderStateCamera, ScreenSpaceConversions } from "@novorender/api";
import { vec2, vec3 } from "gl-matrix";

import { log } from "utils/log";
import { intersectLineWithPlane, Line3D } from "utils/math";
import { LRUCache } from "utils/misc";

import { computeProjectedDepth, getCameraDir } from "../utils";
import { ExternalError } from "./ExternalError";
import {
    type AnchoredSvgData,
    type Arrow,
    type BoxEllipse,
    type ExtendedElements,
    isBoxEllipse,
    isRadiusEllipse,
    type Line,
    type RadiusEllipse,
    type Rect,
    type SvgContent,
    type SvgData,
    type Text,
} from "./types";

const l = log.svgRenderer.lib;

type ParsedTemplate = {
    node: SVGElement;
    viewBox: { x: number; y: number; width: number; height: number } | null;
    dimensions: { width: number; height: number } | null;
};

export class LibSvgRenderer {
    private readonly svgEl: SVGElement;
    private arrowId = 0;
    private camera: RenderStateCamera | null = null;
    private size: { width: number; height: number } = { width: 0, height: 0 };

    private anchoredSvgData: Required<AnchoredSvgData> = {
        ellipses: [],
        lines: [],
        arrows: [],
        texts: [],
        rects: [],
        svgContents: [],
    };

    // Track event listeners for cleanup
    private eventCleanupFunctions: (() => void)[] = [];
    // Capacity 256: covers (icon × color states × perspective-quantized sizes × badge variants)
    // for a typical collection orbit. Strategy F follow-up should reduce template cardinality.
    private readonly templateCache = new LRUCache<string, ParsedTemplate>(256);
    private defsEl: SVGDefsElement | null = null;
    private readonly defsRegistry = new Map<
        string,
        {
            gNode: SVGGElement;
            refCount: number;
        }
    >();

    constructor(svgElement: SVGElement) {
        this.svgEl = svgElement;
    }

    private getOrCreateDefsEl(): SVGDefsElement {
        if (this.defsEl) {
            return this.defsEl;
        }

        const defsEl = document.createElementNS("http://www.w3.org/2000/svg", "defs");
        defsEl.setAttribute("data-svg-instancing-defs", "true");
        this.svgEl.appendChild(defsEl);
        this.defsEl = defsEl;
        return defsEl;
    }

    /**
     * Attach event handlers to an SVG element with automatic cleanup tracking.
     * Uses native SVG hit detection for optimal performance and accuracy.
     *
     * Hit Detection Strategy:
     * - All shapes use native SVG pointer-events for consistent behavior
     * - Filled shapes: detect clicks on the fill area
     * - Stroked shapes: detect clicks on the stroke area
     * - Combined: detect clicks on both fill and stroke
     *
     * @param element - The SVG element to attach handlers to
     * @param handlers - Object containing id and optional event handlers
     * @param options - Optional configuration for hit detection
     */
    private attachEventHandlers(
        element: SVGElement,
        handlers: {
            id?: string;
            onClick?: (id: string, event: PointerEvent) => void;
            onPointerDown?: (id: string, event: PointerEvent) => void;
            onPointerEnter?: (id: string, event: PointerEvent) => void;
            onPointerOut?: (id: string, event: PointerEvent) => void;
        },
        options?: {
            /** Shape-specific pointer-events value for optimized hit detection */
            pointerEvents?:
                | "auto"
                | "fill"
                | "stroke"
                | "painted"
                | "visible"
                | "visibleFill"
                | "visibleStroke"
                | "visiblePainted";
            /** Custom cursor for this element */
            cursor?: string;
        },
    ): void {
        const { id, onClick, onPointerDown, onPointerEnter, onPointerOut } = handlers;

        // If no id, nothing to do
        if (!id) {
            return;
        }

        // Add data-id attribute (even if no handlers, for identification purposes)
        element.dataset.id = id;

        // Use shape-specific pointer-events if provided, otherwise default to "auto"
        // "auto" lets SVG determine hit area based on fill/stroke attributes
        element.style.pointerEvents = options?.pointerEvents ?? "auto";

        // Set cursor for interactive elements
        // Use custom cursor if provided, otherwise default to "pointer" for generic clickable elements
        if (onClick || onPointerEnter || onPointerOut || onPointerDown || options?.cursor) {
            element.style.cursor = options?.cursor ?? "pointer";
        }

        // If no handlers, we can stop here (cursor and pointer-events are already set)
        if (!onClick && !onPointerEnter && !onPointerOut && !onPointerDown) {
            return;
        }

        // Track handlers for cleanup
        const cleanupFunctions: (() => void)[] = [];

        // Attach onClick handler
        if (onClick) {
            const handler = (event: Event) => {
                onClick(id, event as PointerEvent);
            };
            element.addEventListener("click", handler);
            cleanupFunctions.push(() => element.removeEventListener("click", handler));
        }

        // Attach onPointerDown handler
        if (onPointerDown) {
            const handler = (event: Event) => {
                onPointerDown(id, event as PointerEvent);
            };
            element.addEventListener("pointerdown", handler);
            cleanupFunctions.push(() => element.removeEventListener("pointerdown", handler));
        }

        // Attach onPointerEnter handler
        if (onPointerEnter) {
            const handler = (event: Event) => {
                onPointerEnter(id, event as PointerEvent);
            };
            element.addEventListener("pointerenter", handler);
            cleanupFunctions.push(() => element.removeEventListener("pointerenter", handler));
        }

        // Attach onPointerOut handler
        // Use pointerleave (non-bubbling) instead of pointerout so that moving the cursor
        // between nested SVG child elements does not spuriously trigger the leave callback.
        if (onPointerOut) {
            const handler = (event: Event) => {
                onPointerOut(id, event as PointerEvent);
            };
            element.addEventListener("pointerleave", handler);
            cleanupFunctions.push(() => element.removeEventListener("pointerleave", handler));
        }

        // Store cleanup function
        this.eventCleanupFunctions.push(() => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        });
    }

    /**
     * Determine optimal pointer-events value for shapes with fill and/or stroke.
     * Uses native SVG hit detection strategy based on what's visible.
     *
     * @param fill - Fill color or "none"
     * @param stroke - Stroke color or "none"
     * @returns Optimal pointer-events value
     */
    private getPointerEventsForFillStroke(fill: string, stroke: string): "painted" | "fill" | "stroke" {
        const hasFill = fill !== "none";
        const hasStroke = stroke !== "none";

        if (hasFill && hasStroke) {
            return "painted"; // Both fill and stroke - detect on both
        }
        if (hasFill) {
            return "fill"; // Only fill - detect on fill area
        }
        return "stroke"; // Only stroke - detect on stroke area
    }

    /**
     * Transform anchored elements to screen-space elements.
     *
     * Type assertions are used here because we're transforming from anchored types
     * (which have `anchor`/`anchorStart`/`anchorEnd` properties) to screen types
     * (which have `center`/`origin`/`start`/`end` properties). The spread operator
     * copies all properties including event handlers (onClick, onPointerEnter, onPointerOut),
     * which is the desired behavior.
     *
     * The TypeScript type system can't track this transformation automatically because
     * the `Interactive` conditional type makes the relationships complex. The assertions
     * are safe because:
     * 1. We remove anchor-specific properties by overwriting them with screen positions
     * 2. All other properties (including event handlers) are preserved via spread
     * 3. The anchored types are defined as Omit<ScreenType, "position"> & Anchorable
     */

    private hasAnchoredElements(): boolean {
        return (
            this.anchoredSvgData.arrows.length > 0 ||
            this.anchoredSvgData.ellipses.length > 0 ||
            this.anchoredSvgData.lines.length > 0 ||
            this.anchoredSvgData.rects.length > 0 ||
            this.anchoredSvgData.texts.length > 0 ||
            this.anchoredSvgData.svgContents.length > 0
        );
    }

    setRenderState(state: { camera: RenderStateCamera; size: { width: number; height: number } }): void {
        this.camera = state.camera;
        this.size = state.size;
    }

    render = async (params: {
        camera: RenderStateCamera;
        size: { width: number; height: number };
        moved: boolean;
    }): Promise<void> => {
        const {
            size: { width, height },
        } = params;

        if (width <= 0 || height <= 0) {
            l.debug("render - width or height is zero or negative, skipping render");
            return;
        }

        const aspect = width / height;
        const oldAspect = this.size.width / this.size.height;
        if (!params.moved && oldAspect === aspect) {
            // If we haven't moved and haven't resized the screen, no need to render.
            return;
        }

        l.debug("render");
        this.camera = params.camera;
        this.size = { width, height };

        if (this.hasAnchoredElements()) {
            this._clear(false);
            const allElementsWithDepth = this.computeFromAnchored(this.anchoredSvgData);
            const sortedElements = this.sortByDepthAndZIndex(allElementsWithDepth);
            this._draw(sortedElements);
        }
    };

    private computeFromAnchored(anchoredSvgData: AnchoredSvgData): ExtendedElements {
        if (this.camera === null) {
            throw new Error("Camera must be set.");
        }
        const { camera } = this;

        const toScreenPosition = new ScreenSpaceConversions({
            width: this.size.width,
            height: this.size.height,
            camera: this.camera,
        }).worldSpaceToScreenSpaceConverter({ round: false });

        // Separate depth-sorted from regular elements
        const allElements: ExtendedElements = [];
        anchoredSvgData.ellipses?.forEach((element) => {
            const screenPoint = toScreenPosition(element.anchor);

            if (screenPoint === undefined) {
                return;
            }

            if (element.anchorOffset !== undefined) {
                screenPoint[0] += element.anchorOffset[0];
                screenPoint[1] += element.anchorOffset[1];
            }
            if (isRadiusEllipse(element)) {
                // Spread preserves all properties including event handlers, then override center
                const e: RadiusEllipse = {
                    ...element,
                    center: [screenPoint[0], screenPoint[1]],
                } as RadiusEllipse;

                const depth = computeProjectedDepth(element.anchor, camera.position, camera.rotation);
                allElements.push({ type: "ellipses", element: e, depth: depth });
            } else if (isBoxEllipse(element)) {
                const center = screenPoint;
                const radius: vec2 = [(element.max[0] - element.min[0]) / 2, (element.max[1] - element.min[1]) / 2];
                // Spread preserves all properties including event handlers, then override radius and center
                const ellipse: RadiusEllipse = {
                    ...element,
                    radius,
                    center: [center[0], center[1]],
                } as RadiusEllipse;
                const depth = computeProjectedDepth(element.anchor, camera.position, camera.rotation);
                allElements.push({ type: "ellipses", element: ellipse, depth: depth });
            }
        });
        anchoredSvgData.texts?.forEach((element) => {
            const screenPoint = toScreenPosition(element.anchor);
            if (screenPoint === undefined) {
                return;
            }

            if (element.anchorOffset !== undefined) {
                screenPoint[0] += element.anchorOffset[0];
                screenPoint[1] += element.anchorOffset[1];
            }

            // Spread preserves all properties including event handlers, then override origin
            const text: Text = {
                ...element,
                origin: [screenPoint[0], screenPoint[1]],
            } as Text;
            const depth = computeProjectedDepth(element.anchor, camera.position, camera.rotation);
            allElements.push({ type: "texts", element: text, depth: depth });
        });
        anchoredSvgData.rects?.forEach((element) => {
            const screenPoint = toScreenPosition(element.anchor);

            if (screenPoint === undefined) {
                return;
            }

            if (element.anchorOffset !== undefined) {
                screenPoint[0] += element.anchorOffset[0];
                screenPoint[1] += element.anchorOffset[1];
            }

            const x = screenPoint[0] - element.width / 2;
            const y = screenPoint[1] - element.height / 2;

            // Spread preserves all properties including event handlers, then override x and y
            const rect: Rect = {
                ...element,
                x,
                y,
            } as Rect;
            const depth = computeProjectedDepth(element.anchor, camera.position, camera.rotation);
            allElements.push({ type: "rects", element: rect, depth: depth });
        });
        anchoredSvgData.lines?.forEach((element) => {
            const lineClipped = this.clipLine({ start: element.anchorStart, end: element.anchorEnd }, camera);
            if (lineClipped === null) {
                return;
            }

            const screenPointStart = toScreenPosition(lineClipped.start);
            const screenPointEnd = toScreenPosition(lineClipped.end);

            if (screenPointStart === undefined || screenPointEnd === undefined) {
                return;
            }

            // Spread preserves all properties including event handlers, then override start and end
            const line: Line = {
                ...element,
                start: [screenPointStart[0], screenPointStart[1]],
                end: [screenPointEnd[0], screenPointEnd[1]],
            } as Line;
            // Use midpoint for depth calculation
            const midpoint = vec3.create();
            vec3.lerp(midpoint, element.anchorStart, element.anchorEnd, 0.5);
            const depth = computeProjectedDepth(midpoint, camera.position, camera.rotation);
            allElements.push({ type: "lines", element: line, depth: depth });
        });
        anchoredSvgData.arrows?.forEach((element) => {
            const lineClipped = this.clipLine({ start: element.anchorStart, end: element.anchorEnd }, camera);
            if (lineClipped === null) {
                return;
            }

            const screenPointStart = toScreenPosition(lineClipped.start);
            const screenPointEnd = toScreenPosition(lineClipped.end);

            if (screenPointStart === undefined || screenPointEnd === undefined) {
                return;
            }

            // Spread preserves all properties including event handlers, then override start and end
            const arrow: Arrow = {
                ...element,
                start: [screenPointStart[0], screenPointStart[1]],
                end: [screenPointEnd[0], screenPointEnd[1]],
            } as Arrow;
            // Use midpoint for depth calculation
            const midpoint = vec3.create();
            vec3.lerp(midpoint, element.anchorStart, element.anchorEnd, 0.5);
            const depth = computeProjectedDepth(midpoint, camera.position, camera.rotation);
            allElements.push({ type: "arrows", element: arrow, depth: depth });
        });

        anchoredSvgData.svgContents?.forEach((element) => {
            const screenPoint = toScreenPosition(element.anchor);

            if (screenPoint === undefined) {
                return;
            }

            if (element.anchorOffset !== undefined) {
                // Convert anchorOffset from viewBox coordinates to pixel coordinates
                const anchorOffset =
                    element.anchorOffsetCoordinateType === "viewBox"
                        ? this.convertViewBoxOffsetToPixels(
                              [element.anchorOffset[0], element.anchorOffset[1]],
                              element.content,
                          )
                        : [element.anchorOffset[0], element.anchorOffset[1]];

                // Necessary to scale the svg object around the anchor point. Refer to drawSvgContent for more details.
                if (element.scale !== undefined) {
                    anchorOffset[0] *= element.scale;
                    anchorOffset[1] *= element.scale;
                }
                screenPoint[0] += anchorOffset[0];
                screenPoint[1] += anchorOffset[1];
            }

            const content: SvgContent = {
                ...element,
                origin: [screenPoint[0], screenPoint[1]],
            } as SvgContent;
            const depth = computeProjectedDepth(element.anchor, camera.position, camera.rotation);
            allElements.push({ type: "svgContents", element: content, depth: depth });
        });

        return allElements;
    }

    /**
     * Sort depth-sorted elements into a flat array by projected depth and zIndex (prioritized).
     * Returns elements sorted from far to near (back-to-front rendering).
     */
    private sortByDepthAndZIndex(allElements: ExtendedElements): ExtendedElements {
        // Sort by depth (near to far for front-to-back rendering)
        allElements.sort((a, b) => {
            const aZIndex = a.element.zIndex ?? 0;
            const bZIndex = b.element.zIndex ?? 0;
            if (aZIndex === bZIndex) {
                return (b.depth ?? 0) - (a.depth ?? 0);
            }
            return aZIndex - bZIndex;
        });

        return allElements;
    }

    private saveAnchoredSvgData(anchoredSvgData: AnchoredSvgData): void {
        this.anchoredSvgData.ellipses = anchoredSvgData.ellipses ?? [];
        this.anchoredSvgData.texts = anchoredSvgData.texts ?? [];
        this.anchoredSvgData.rects = anchoredSvgData.rects ?? [];
        this.anchoredSvgData.lines = anchoredSvgData.lines ?? [];
        this.anchoredSvgData.arrows = anchoredSvgData.arrows ?? [];
        this.anchoredSvgData.svgContents = anchoredSvgData.svgContents ?? [];
    }

    private collateByZIndex(svgData: SvgData): SvgData[] {
        const zIndices = new Map<number, SvgData>();

        Object.entries(svgData).forEach(([key, value]) => {
            value.forEach((element) => {
                const k = key as keyof SvgData;
                const zIndex = element.zIndex ?? 0;
                let zIndexGroup = zIndices.get(zIndex);

                // If there are no entries in this zIndexGroup yet, create an empty one
                if (zIndexGroup === undefined) {
                    zIndexGroup = {};
                }

                // If there are no entries for this shape type yet, create an empty one
                if (zIndexGroup[k] === undefined) {
                    zIndexGroup[k] = [];
                }

                // @ts-expect-error - There's not a good way for TS to know that
                // we're putting element into the right key here (e.g. key of
                // "rect" only gets elements of type `Rect`) unless we were also
                // to do a bunch of type checking here. But we loop over each
                // key, and then over each of the elements under that key so for
                // this loop structure it should be safe.
                zIndexGroup[k].push(element);

                zIndices.set(zIndex, zIndexGroup);
            });
        });

        return Array.from(zIndices.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([_, value]) => value);
    }

    async draw(svgData: SvgData): Promise<void> {
        // Then, draw regular elements by zIndex
        const zIndexGroups = this.collateByZIndex(svgData);
        const allElements: ExtendedElements = [];
        zIndexGroups.forEach((zIndexGroup) => {
            zIndexGroup.ellipses?.forEach((ellipse) => {
                allElements.push({ type: "ellipses", element: ellipse });
            });
            zIndexGroup.lines?.forEach((line) => {
                allElements.push({ type: "lines", element: line });
            });

            zIndexGroup.arrows?.forEach((arrow) => {
                allElements.push({ type: "arrows", element: arrow });
            });

            zIndexGroup.texts?.forEach((text) => {
                allElements.push({ type: "texts", element: text });
            });

            zIndexGroup.rects?.forEach((rect) => {
                allElements.push({ type: "rects", element: rect });
            });

            zIndexGroup.svgContents?.forEach((content) => {
                allElements.push({ type: "svgContents", element: content });
            });
        });

        this._draw(allElements);
    }

    private _draw(allElements: ExtendedElements): void {
        // First, draw depth-sorted elements in sorted order (far to near)
        allElements.forEach(({ type, element }) => {
            // If element has zIndex, it should be drawn in the zIndex batch with depth based ordering as well.
            switch (type) {
                case "ellipses":
                    this.drawEllipse(element as RadiusEllipse | BoxEllipse);
                    break;
                case "lines":
                    this.drawLine(element as Line);
                    break;
                case "arrows":
                    this.drawArrow(element as Arrow);
                    break;
                case "texts":
                    this.drawText(element as Text);
                    break;
                case "rects":
                    this.drawRect(element as Rect);
                    break;
                case "svgContents":
                    if ((element as SvgContent).useInstancing) {
                        this.drawSvgInstance(element as SvgContent);
                    } else {
                        this.drawSvgContent(element as SvgContent);
                    }
                    break;
            }
        });
    }

    async drawAnchored(anchoredSvgData: AnchoredSvgData): Promise<void> {
        const hasContent =
            (anchoredSvgData.ellipses?.length ?? 0) > 0 ||
            (anchoredSvgData.texts?.length ?? 0) > 0 ||
            (anchoredSvgData.rects?.length ?? 0) > 0 ||
            (anchoredSvgData.lines?.length ?? 0) > 0 ||
            (anchoredSvgData.arrows?.length ?? 0) > 0 ||
            (anchoredSvgData.svgContents?.length ?? 0) > 0;

        if (!hasContent) {
            // Even when there is no content, update the stored anchoredSvgData
            // so that subsequent renders do not redraw stale elements.
            this.saveAnchoredSvgData(anchoredSvgData);
            return;
        }
        // Store latest svgData so that we can redraw it if animation is enabled.
        this.saveAnchoredSvgData(anchoredSvgData);
        const allElementsWithDepth = this.computeFromAnchored(anchoredSvgData);
        const sortedElements = this.sortByDepthAndZIndex(allElementsWithDepth);
        this._draw(sortedElements);
    }

    drawEllipse(ellipse: BoxEllipse | RadiusEllipse): void {
        const v = ellipse;

        let rX: number;
        let rY: number;
        let cX: number;
        let cY: number;

        if (isRadiusEllipse(v)) {
            rX = v.radius[0];
            rY = v.radius[1];
            cX = v.center[0];
            cY = v.center[1];
        } else {
            // Otherwise it's a box ellipse
            const minX = v.min[0];
            const maxX = v.max[0];
            const minY = v.min[1];
            const maxY = v.max[1];

            // If the min point is greater than the max point, throw an error.
            if (minX > maxX || minY > maxY) {
                throw new ExternalError(
                    `Ellipse cannot have min point greater than max point.
  min_point: (${minX}, ${minY})
  max_point: (${maxX}, ${maxY})`,
                );
            }

            rX = (maxX - minX) / 2;
            rY = (maxY - minY) / 2;
            cX = minX + rX;
            cY = minY + rY;
        }

        const fill = v.fill ?? "none";
        const { strokeWidth, stroke } = v;

        // Provide defaults for required SVG attributes
        const strokeWidthValue = strokeWidth ?? 0;
        const strokeValue = stroke ?? "none";

        const svgEllipse = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
        svgEllipse.setAttribute("stroke", strokeValue);
        svgEllipse.setAttribute("stroke-width", strokeWidthValue.toString());
        svgEllipse.setAttribute("fill", fill);
        if (v.opacity !== undefined) {
            svgEllipse.setAttribute("opacity", v.opacity.toString());
        }
        svgEllipse.setAttribute("cx", cX.toString());
        svgEllipse.setAttribute("cy", cY.toString());
        svgEllipse.setAttribute("rx", `${rX}`);
        svgEllipse.setAttribute("ry", `${rY}`);

        // Attach event handlers with optimized native SVG hit detection
        // For ellipses: use appropriate pointer-events based on fill/stroke configuration
        const pointerEvents = this.getPointerEventsForFillStroke(fill, strokeValue);

        this.attachEventHandlers(
            svgEllipse,
            {
                id: v.id,
                onClick: v.onClick,
                onPointerDown: v.onPointerDown,
                onPointerEnter: v.onPointerEnter,
                onPointerOut: v.onPointerOut,
            },
            { pointerEvents, cursor: v.cursor },
        );

        this.svgEl.append(svgEllipse);
    }

    drawLine(line: Line): void {
        const v = line;
        const startX = v.start[0];
        const startY = v.start[1];

        const endX = v.end[0];
        const endY = v.end[1];
        const { strokeWidth, stroke } = v;

        // Provide defaults for required SVG attributes
        const strokeWidthValue = strokeWidth ?? 1;
        const strokeValue = stroke ?? "black";

        const svgLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
        svgLine.setAttribute("stroke", strokeValue);
        svgLine.setAttribute("stroke-width", strokeWidthValue.toString());
        svgLine.setAttribute("x1", startX.toString());
        svgLine.setAttribute("y1", startY.toString());
        svgLine.setAttribute("x2", endX.toString());
        svgLine.setAttribute("y2", endY.toString());

        if (v.opacity) {
            svgLine.setAttribute("opacity", v.opacity.toString());
        }
        if (v.strokeDasharray) {
            svgLine.setAttribute("stroke-dasharray", v.strokeDasharray);
        }
        if (v.strokeLinecap) {
            svgLine.setAttribute("stroke-linecap", v.strokeLinecap);
        }

        // Attach event handlers with optimized native SVG hit detection
        // For lines: use "stroke" for precise hit detection on the line stroke
        this.attachEventHandlers(
            svgLine,
            {
                id: v.id,
                onClick: v.onClick,
                onPointerDown: v.onPointerDown,
                onPointerEnter: v.onPointerEnter,
                onPointerOut: v.onPointerOut,
            },
            { pointerEvents: "stroke" },
        );

        this.svgEl.appendChild(svgLine);
    }

    drawArrow(arrow: Arrow): void {
        const { arrowId } = this;
        this.arrowId += 1;
        const defs =
            this.svgEl.querySelector("defs") ||
            this.svgEl.insertBefore(
                document.createElementNS("http://www.w3.org/2000/svg", "defs"),
                this.svgEl.firstChild,
            );

        const v = arrow;
        const startX = v.start[0];
        const startY = v.start[1];

        const endX = v.end[0];
        const endY = v.end[1];
        const { strokeWidth, stroke } = v;

        // Provide defaults for required SVG attributes
        const strokeWidthValue = strokeWidth ?? 1;
        const strokeValue = stroke ?? "black";

        if (!defs.querySelector(`#arrowhead-${arrowId}`)) {
            const markerEl = document.createElementNS("http://www.w3.org/2000/svg", "marker");
            markerEl.setAttribute("id", `arrowhead-${arrowId}`);
            markerEl.setAttribute("viewBox", "0 0 10 10");
            markerEl.setAttribute("refX", "7");
            markerEl.setAttribute("refY", "5");
            markerEl.setAttribute("markerWidth", "6");
            markerEl.setAttribute("markerHeight", "3");
            markerEl.setAttribute("orient", "auto-start-reverse");
            markerEl.setAttribute("fill", strokeValue);

            const pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
            pathEl.setAttribute("d", "M 0 0 L 10 5 L 0 10 z");

            markerEl.appendChild(pathEl);
            defs.append(markerEl);
        }

        const svgArrow = document.createElementNS("http://www.w3.org/2000/svg", "line");
        svgArrow.setAttribute("stroke", strokeValue);
        svgArrow.setAttribute("stroke-width", strokeWidthValue.toString());
        svgArrow.setAttribute("x1", startX.toString());
        svgArrow.setAttribute("y1", startY.toString());
        svgArrow.setAttribute("x2", endX.toString());
        svgArrow.setAttribute("y2", endY.toString());
        svgArrow.setAttribute("marker-end", `url(#arrowhead-${arrowId})`);

        // Attach event handlers with optimized native SVG hit detection
        // For arrows: use "stroke" for hit detection on the line stroke
        // Note: marker (arrowhead) hit detection requires pointer-events on the marker element itself
        this.attachEventHandlers(
            svgArrow,
            {
                id: v.id,
                onClick: v.onClick,
                onPointerDown: v.onPointerDown,
                onPointerEnter: v.onPointerEnter,
                onPointerOut: v.onPointerOut,
            },
            { pointerEvents: "stroke" },
        );

        this.svgEl.appendChild(svgArrow);
    }

    drawText(text: Text): void {
        const v = text;
        const tX = v.origin[0];
        const tY = v.origin[1];
        const font = v.font ?? "21px tahoma";

        const svgText = document.createElementNS("http://www.w3.org/2000/svg", "text");
        svgText.setAttribute("fill", v.fill);
        svgText.style.font = font;
        svgText.setAttribute("x", tX.toString());
        svgText.setAttribute("y", tY.toString());
        svgText.textContent = v.text;

        // Attach event handlers with optimized native SVG hit detection
        // For text: use "fill" to detect on the filled text glyphs (most accurate for text)
        this.attachEventHandlers(
            svgText,
            {
                id: v.id,
                onClick: v.onClick,
                onPointerDown: v.onPointerDown,
                onPointerEnter: v.onPointerEnter,
                onPointerOut: v.onPointerOut,
            },
            { pointerEvents: "fill" },
        );

        this.svgEl.append(svgText);
    }

    drawRect(rect: Rect): void {
        const { width, height, x, y, radius } = rect;
        const rX = radius?.[0] ?? 0;
        const rY = radius?.[1] ?? 0;
        const fill = rect.fill ?? "none";
        const stroke = rect.stroke;
        const strokeWidth = rect.strokeWidth;

        // Provide defaults for required SVG attributes
        const strokeWidthValue = strokeWidth ?? 1;
        const strokeValue = stroke ?? "none";

        const svgRect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        svgRect.setAttribute("width", width.toString());
        svgRect.setAttribute("height", height.toString());
        svgRect.setAttribute("x", x.toString());
        svgRect.setAttribute("y", y.toString());
        svgRect.setAttribute("rx", rX.toString());
        svgRect.setAttribute("ry", rY.toString());
        svgRect.setAttribute("fill", fill);
        if (rect.opacity !== undefined) {
            svgRect.setAttribute("opacity", rect.opacity.toString());
        }
        svgRect.setAttribute("stroke", strokeValue);
        svgRect.setAttribute("stroke-width", strokeWidthValue.toString());

        // Attach event handlers with optimized native SVG hit detection
        // For rectangles: use appropriate pointer-events based on fill/stroke configuration
        const pointerEvents = this.getPointerEventsForFillStroke(fill, strokeValue);

        this.attachEventHandlers(
            svgRect,
            {
                id: rect.id,
                onClick: rect.onClick,
                onPointerDown: rect.onPointerDown,
                onPointerEnter: rect.onPointerEnter,
                onPointerOut: rect.onPointerOut,
            },
            { pointerEvents, cursor: rect.cursor },
        );

        this.svgEl.append(svgRect);
    }

    private getOrCreateTemplate(content: string): ParsedTemplate {
        const cached = this.templateCache.get(content);
        if (cached) {
            return cached;
        }

        performance.mark("svg-parse");

        let contentWithXmlNs = content;
        if (!contentWithXmlNs.includes("xmlns")) {
            contentWithXmlNs = contentWithXmlNs.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
        }

        const doc = new DOMParser().parseFromString(contentWithXmlNs, "image/svg+xml");
        const node = doc.documentElement as unknown as SVGElement;
        const parserError = doc.querySelector("parsererror");
        if (parserError || node.nodeName === "parsererror") {
            throw new ExternalError(`Invalid SVG content: ${parserError?.textContent || "Parse error"}`);
        }

        // Sanitization happens exactly once per unique SVG content string.
        this.sanitizeSvgElement(node);

        // viewBox is in user units, so detached node baseVal is reliable.
        const svgNode = node as unknown as SVGSVGElement;
        const viewBoxBase = svgNode.viewBox?.baseVal;
        // Width/height may use relative units; parse against live container dimensions.
        const containerWidth = this.svgEl.clientWidth;
        const containerHeight = this.svgEl.clientHeight;

        const template: ParsedTemplate = {
            node,
            viewBox:
                viewBoxBase && viewBoxBase.width > 0 && viewBoxBase.height > 0
                    ? {
                          x: viewBoxBase.x,
                          y: viewBoxBase.y,
                          width: viewBoxBase.width,
                          height: viewBoxBase.height,
                      }
                    : null,
            dimensions: this.parseSvgDimensions(contentWithXmlNs, containerWidth, containerHeight),
        };

        this.templateCache.set(content, template);
        return template;
    }

    /**
     * Parse width and height attributes from SVG string, supporting px, %, and em units.
     * @param svgString - The SVG content as a string
     * @param containerWidth - Optional container width for percentage calculations
     * @param containerHeight - Optional container height for percentage calculations
     * @returns Object with width and height or null if not found/cannot be parsed
     */
    private parseSvgDimensions(
        svgString: string,
        containerWidth?: number,
        containerHeight?: number,
    ): { width: number; height: number } | null {
        const openingSvgTagMatch = svgString.match(/<svg\b[^>]*>/i);
        if (!openingSvgTagMatch) {
            return null;
        }
        const openingSvgTag = openingSvgTagMatch[0];

        // Match number with optional unit (px, em, %)
        const widthMatch = openingSvgTag.match(/width\s*=\s*["']?(\d+(?:\.\d+)?)(px|em|%)?["']?/i);
        const heightMatch = openingSvgTag.match(/height\s*=\s*["']?(\d+(?:\.\d+)?)(px|em|%)?["']?/i);

        if (!widthMatch || !heightMatch) {
            return null;
        }

        const width = this.parseLength(widthMatch[1], widthMatch[2], containerWidth);
        const height = this.parseLength(heightMatch[1], heightMatch[2], containerHeight);

        if (width === null || height === null) {
            return null;
        }

        return { width, height };
    }

    /**
     * Parse a length value with unit and convert to pixels.
     * @param value - The numeric value as a string
     * @param unit - The unit (px, em, %, or undefined for unitless)
     * @param containerSize - The container size for percentage calculations
     * @returns The value in pixels or null if cannot be parsed
     */
    private parseLength(value: string, unit: string | undefined, containerSize?: number): number | null {
        const numValue = Number(value);

        if (isNaN(numValue)) {
            return null;
        }

        // Handle different units
        switch (unit) {
            case undefined:
            case "px":
                return numValue;

            case "%":
                // Need container size to calculate
                if (containerSize === undefined) {
                    return null; // Can't calculate without container
                }
                return (numValue / 100) * containerSize;

            case "em":
                // Measure em in the actual DOM context
                return this.convertEmToPixels(numValue);

            default:
                // Unsupported unit
                return null;
        }
    }

    /**
     * Convert em units to pixels by measuring in the actual DOM context.
     * @param emValue - The value in em units
     * @returns The value in pixels
     */
    private convertEmToPixels(emValue: number): number {
        // Create a temporary element to measure em in the actual DOM context
        const temp = document.createElement("div");
        temp.style.position = "absolute";
        temp.style.visibility = "hidden";
        temp.style.width = `${emValue}em`;

        // Append to the SVG container to inherit its font context
        this.svgEl.appendChild(temp);
        const pixels = temp.offsetWidth;
        this.svgEl.removeChild(temp);

        return pixels;
    }

    /**
     * Convert anchor offset from viewBox coordinates to pixel coordinates.
     * If viewBox or dimensions cannot be parsed, returns the offset unchanged (assumes 1:1 mapping).
     *
     * @param anchorOffset - The offset in viewBox coordinates
     * @param svgString - The SVG content string
     * @returns The offset in pixel coordinates
     */
    private convertViewBoxOffsetToPixels(anchorOffset: vec2, svgString: string): vec2 {
        const { viewBox, dimensions } = this.getOrCreateTemplate(svgString);

        // If we can't parse viewBox or dimensions, assume 1:1 mapping
        if (!viewBox || !dimensions) {
            return anchorOffset;
        }

        // Convert from viewBox coordinates to pixel coordinates
        const pixelX = ((anchorOffset[0] - viewBox.x) / viewBox.width) * dimensions.width;
        const pixelY = ((anchorOffset[1] - viewBox.y) / viewBox.height) * dimensions.height;

        return [pixelX, pixelY];
    }

    private sanitizeSvgElement(element: Element): void {
        // Remove dangerous elements
        const dangerousElements = [
            "script",
            "foreignObject",
            "iframe",
            "object",
            "embed",
            "style",
            "link",
            "form",
            "input",
            "textarea",
            "button",
            "video",
            "audio",
            "img",
            "picture",
        ];
        dangerousElements.forEach((tagName) => {
            const elements = element.querySelectorAll(tagName);
            elements.forEach((el) => el.remove());
        });

        // Remove event handler attributes
        const eventHandlerPattern = /^on/i;
        // Allow only fragment identifiers (#id) for internal SVG references (e.g., <use href="#id">)
        const safeHrefPattern = /^#[a-zA-Z][\w.-]*$/;

        const sanitizeAttributes = (el: Element) => {
            const attributes = Array.from(el.attributes);
            attributes.forEach((attr) => {
                // Remove event handlers
                if (eventHandlerPattern.test(attr.name)) {
                    el.removeAttribute(attr.name);
                }
                // Remove all hrefs except safe internal fragment references
                if (attr.name === "href" || attr.name === "xlink:href") {
                    if (!safeHrefPattern.test(attr.value.trim())) {
                        el.removeAttribute(attr.name);
                    }
                }
            });
        };

        // Sanitize the root element and all descendants
        sanitizeAttributes(element);
        element.querySelectorAll("*").forEach((el) => {
            sanitizeAttributes(el);
        });
    }

    private createTemplateHash(content: string): string {
        // FNV-1a 32-bit hash; deterministic and sufficient for template IDs.
        let hash = 0x811c9dc5;
        for (let i = 0; i < content.length; i += 1) {
            hash ^= content.charCodeAt(i);
            hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        }
        return (hash >>> 0).toString(16);
    }

    private ensureDefTemplate(content: string): string {
        const hash = this.createTemplateHash(content);
        const templateId = `svg-inst-${hash}`;
        const existing = this.defsRegistry.get(templateId);
        if (existing) {
            existing.refCount += 1;
            return templateId;
        }

        const template = this.getOrCreateTemplate(content);
        // Clone cached node so ensureDefTemplate does not mutate cache entries.
        const templateClone = template.node.cloneNode(true) as SVGElement;
        if (templateClone instanceof SVGSVGElement) {
            // Keep template size stable when the source SVG omits width/height.
            // Browsers default nested SVGs to 300x150 otherwise, which inflates marker size.
            const intrinsicWidth = template.dimensions?.width ?? template.viewBox?.width;
            const intrinsicHeight = template.dimensions?.height ?? template.viewBox?.height;
            if (templateClone.getAttribute("width") === null && intrinsicWidth !== undefined) {
                templateClone.setAttribute("width", String(intrinsicWidth));
            }
            if (templateClone.getAttribute("height") === null && intrinsicHeight !== undefined) {
                templateClone.setAttribute("height", String(intrinsicHeight));
            }
        }
        const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
        group.setAttribute("id", templateId);
        group.appendChild(templateClone);
        const defsEl = this.getOrCreateDefsEl();
        defsEl.appendChild(group);
        this.defsRegistry.set(templateId, { gNode: group, refCount: 1 });

        return templateId;
    }

    private decrementTemplateRefCount(templateId: string): void {
        const entry = this.defsRegistry.get(templateId);
        if (!entry) {
            return;
        }
        entry.refCount -= 1;
        if (entry.refCount <= 0) {
            entry.gNode.remove();
            this.defsRegistry.delete(templateId);
        }
    }

    drawSvgInstance(svgContent: SvgContent): void {
        const templateId = this.ensureDefTemplate(svgContent.content);
        const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
        use.setAttribute("href", `#${templateId}`);
        let [x, y] = svgContent.origin;
        const scale = svgContent.scale ?? 1;
        x /= scale;
        y /= scale;

        use.setAttribute("x", Math.round(x).toString());
        use.setAttribute("y", Math.round(y).toString());
        use.setAttribute("transform", `scale(${scale})`);
        use.style.pointerEvents =
            svgContent.onClick || svgContent.onPointerDown || svgContent.onPointerEnter || svgContent.onPointerOut
                ? "visiblePainted"
                : "none";
        if (svgContent.opacity !== undefined) {
            use.setAttribute("opacity", String(svgContent.opacity));
        }
        if (svgContent.cursor) {
            use.style.cursor = svgContent.cursor;
        }
        if (svgContent.cssVars) {
            for (const [key, value] of Object.entries(svgContent.cssVars)) {
                use.style.setProperty(key, value);
            }
        }
        this.attachEventHandlers(
            use,
            {
                id: svgContent.id,
                onClick: svgContent.onClick,
                onPointerDown: svgContent.onPointerDown,
                onPointerEnter: svgContent.onPointerEnter,
                onPointerOut: svgContent.onPointerOut,
            },
            { pointerEvents: "visiblePainted", cursor: svgContent.cursor },
        );

        this.svgEl.appendChild(use);
    }

    drawSvgContent(svgContent: SvgContent): void {
        let x = svgContent.origin[0];
        let y = svgContent.origin[1];
        const scale = svgContent.scale ?? 1;
        const opacity = svgContent.opacity ?? 1;
        // It is necessary to scale down the position of the svg content since the transform: scale(x) is applied to the entire svg element, hence the position of the svg content is also scaled. This way we can scale the svg content around the anchor point.
        x /= scale;
        y /= scale;
        const template = this.getOrCreateTemplate(svgContent.content);
        const svg = template.node.cloneNode(true) as SVGElement;

        svg.setAttribute("x", Math.round(x).toString());
        svg.setAttribute("y", Math.round(y).toString());
        svg.setAttribute("transform", `scale(${scale})`);
        svg.setAttribute("opacity", opacity.toString());

        // Since the container SVG might not have fill or stroke attributes, we are assuming that any of its children will have them.
        const children = svg.querySelectorAll("*");
        let fill = "none";
        let stroke = "none";
        children.forEach((child) => {
            const childFill = child.getAttribute("fill");
            const childStroke = child.getAttribute("stroke");
            if (fill === "none" && childFill) {
                fill = childFill;
            }
            if (stroke === "none" && childStroke) {
                stroke = childStroke;
            }
            if (fill !== "none" && stroke !== "none") {
                return;
            }
        });
        const pointerEvents = this.getPointerEventsForFillStroke(fill, stroke);
        this.attachEventHandlers(
            svg,
            {
                id: svgContent.id,
                onClick: svgContent.onClick,
                onPointerDown: svgContent.onPointerDown,
                onPointerEnter: svgContent.onPointerEnter,
                onPointerOut: svgContent.onPointerOut,
            },
            { pointerEvents, cursor: svgContent.cursor },
        );

        this.svgEl.appendChild(svg);
    }

    private clipLine(line: Line3D, camera: RenderStateCamera): Line3D | null {
        const { start, end } = line;
        const direction = getCameraDir(camera.rotation);
        const { position, near } = camera;
        const coplanarPoint = vec3.create();
        vec3.scaleAndAdd(coplanarPoint, position, direction, near);
        const isStartFront = vec3.dot(direction, vec3.sub(vec3.create(), start, coplanarPoint)) > 0;
        const isEndFront = vec3.dot(direction, vec3.sub(vec3.create(), end, coplanarPoint)) > 0;
        if (isStartFront && isEndFront) {
            return line;
        }
        if (!isStartFront && !isEndFront) {
            return null;
        }
        const intersectPoint = intersectLineWithPlane(start, end, direction, coplanarPoint);
        if (intersectPoint === null) {
            return null;
        }
        if (isStartFront) {
            return { start, end: intersectPoint };
        }
        return { start: intersectPoint, end };
    }

    private _clear(clearInternalSvgData: boolean): void {
        if (clearInternalSvgData) {
            this.anchoredSvgData = {
                ellipses: [],
                lines: [],
                arrows: [],
                texts: [],
                rects: [],
                svgContents: [],
            };
        }

        // Clean up all event listeners to prevent memory leaks
        this.eventCleanupFunctions.forEach((cleanup) => cleanup());
        this.eventCleanupFunctions = [];

        const childrenToRemove = Array.from(this.svgEl.children).filter((child) => child !== this.defsEl);
        childrenToRemove.forEach((child) => {
            if (child instanceof SVGUseElement) {
                const hrefValue = child.getAttribute("href") ?? child.getAttribute("xlink:href");
                const templateId = hrefValue?.startsWith("#") ? hrefValue.slice(1) : hrefValue;
                if (templateId) {
                    this.decrementTemplateRefCount(templateId);
                }
            }

            child.remove();
        });

        if (this.defsEl && this.defsEl.childElementCount === 0) {
            this.defsEl.remove();
            this.defsEl = null;
        }
        this.arrowId = 0;
    }

    clear(): void {
        this._clear(true);
    }

    hasContent(): boolean {
        return Array.from(this.svgEl.children).some((child) => child !== this.defsEl);
    }
}
